import Modal from '@/components/atoms/Modal';
import { CreateManagerPayload } from '@/services/innovorder/user/user.type';
import React, { useEffect, useState, useMemo } from 'react';
import { Input } from '@/components/atoms/Form/Input';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useCreateManagerMutation } from '@/services/innovorder/user/user.endpoints';
import { useRouteMatch, useHistory } from 'react-router';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { SvyButton } from '@/components/savory/SvyButton';
import { SvySwitch } from '@/components/savory/SvySwitch';
import { computeText } from '@/locales/utils';
import { useIntl, FormattedMessage } from 'react-intl';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { Label } from '@/components/atoms/Label';
import { InputsContainer, SwitchWrapper } from './CreateUserForm.style';

interface CreateUserFormProps {
    isOpen: boolean;
    onHide: () => void;
}
const CreateUserForm = ({ isOpen, onHide }: CreateUserFormProps): JSX.Element => {
    const intl = useIntl();
    const history = useHistory();
    const {
        params: { brandId },
    } = useRouteMatch<{ brandId: string }>();

    const resolver = useMemo(
        () =>
            yupResolver(
                Yup.object().shape({
                    firstName: Yup.string().required(computeText(intl, 'field.required')),
                    lastName: Yup.string().required(computeText(intl, 'field.required')),
                    phone: Yup.string()
                        .test(
                            'validPhoneNumber',
                            computeText(intl, 'user.create.error.phone.valid'),
                            (value) => !!value && isValidPhoneNumber(value, 'FR'),
                        )
                        .required(computeText(intl, 'field.required')),
                    email: Yup.string()
                        .email(computeText(intl, 'field.invalidEmail'))
                        .required(computeText(intl, 'field.required')),
                    letUserChoosePassword: Yup.boolean(),
                    password: Yup.string().when('letUserChoosePassword', {
                        is: true,
                        then: () => Yup.string().nullable(),
                        otherwise: () =>
                            Yup.string()
                                .min(12, computeText(intl, 'user.create.error.password.min'))
                                .required(computeText(intl, 'field.required')),
                    }),
                    confirmPassword: Yup.string().when('letUserChoosePassword', {
                        is: true,
                        then: () => Yup.string().nullable(),
                        otherwise: () =>
                            Yup.string()
                                .oneOf(
                                    [Yup.ref('password'), null],
                                    computeText(intl, 'user.create.error.passwordConfirm.sameAsPassword'),
                                )
                                .required(computeText(intl, 'field.required')),
                    }),
                }),
            ),
        [intl],
    );

    type CreateUserFormData = Omit<
        CreateManagerPayload,
        'password' | 'confirmPassword' | 'badgeNumber' | 'brandId' | 'role'
    > & {
        password: string;
        confirmPassword: string;
        letUserChoosePassword?: boolean;
    };

    const form = useForm<CreateUserFormData>({
        resolver,
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: '',
            letUserChoosePassword: true,
        },
        mode: 'onBlur',
    });

    const letUserChoosePassword = form.watch('letUserChoosePassword');

    useEffect(() => {
        if (letUserChoosePassword) {
            // Only clear password fields if they're empty to avoid losing user input
            const currentPassword = form.getValues('password');
            const currentConfirmPassword = form.getValues('confirmPassword');
            if (!currentPassword && !currentConfirmPassword) {
                form.clearErrors(['password', 'confirmPassword']);
                form.setValue('password', '');
                form.setValue('confirmPassword', '');
            } else {
                // If user has entered data, don't clear it - just clear errors
                form.clearErrors(['password', 'confirmPassword']);
            }
        } else {
            // Only trigger validation if fields have been touched to respect onBlur mode
            const touchedFields = form.formState.touchedFields;
            if (touchedFields.password || touchedFields.confirmPassword) {
                form.trigger(['password', 'confirmPassword']);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [letUserChoosePassword]);

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
    const [createManager, { data: createdUser, isSuccess, error, reset, isLoading: isCreateLoading }] =
        useCreateManagerMutation();

    const onSubmit = async (data: CreateUserFormData) => {
        const { letUserChoosePassword: shouldLetUserChoosePassword, password, confirmPassword, ...payload } = data;
        const requestPayload: CreateManagerPayload = {
            ...payload,
            badgeNumber: 0, // Default badge number, backend may handle this
            brandId: Number(brandId),
            role: 'brand',
            password: shouldLetUserChoosePassword ? null : password || '',
            confirmPassword: shouldLetUserChoosePassword ? null : confirmPassword || '',
            ...(shouldLetUserChoosePassword ? { letUserChoosePassword: true } : {}),
        };
        await createManager(requestPayload).unwrap();
    };

    useEffect(() => {
        const errorObject = error && ((error as FetchBaseQueryError).data as { code: string; message: string });
        if (errorObject?.code === 'duplicate_email') {
            form.setError('email', { message: errorObject.message });
        }
        if (errorObject?.code === 'invalid_password') {
            form.setError('password', { message: errorObject.message });
        }
    }, [error, form]);
    useEffect(() => {
        if (isSuccess && createdUser?.userId) {
            history.push(`/brands/${brandId}/users/${createdUser?.userId}/access_flags`);
        }
    }, [isSuccess, createdUser, brandId, history]);

    const handleHide = () => {
        const values = form.getValues();
        const isFormEmpty =
            values.firstName === '' &&
            values.lastName === '' &&
            values.email === '' &&
            values.phone === '' &&
            values.password === '' &&
            values.confirmPassword === '' &&
            values.letUserChoosePassword === true;
        if (isFormEmpty) {
            onHide();
            form.reset();
            reset();
            return;
        }
        setIsConfirmationModalOpen(true);
    };

    const handleConfirmationHide = () => {
        setIsConfirmationModalOpen(false);
    };
    const forceHide = () => {
        form.reset();
        onHide();
        handleConfirmationHide();
    };

    return (
        <Modal
            isOpen={isOpen}
            onHide={handleHide}
            width={700}
            title={'user.create.title'}
            buttons={
                !isSuccess
                    ? [
                          <SvyButton key="save" isLoading={isCreateLoading} onClick={form.handleSubmit(onSubmit)}>
                              <FormattedMessage id="button.save" />
                          </SvyButton>,
                      ]
                    : []
            }
        >
            <Modal
                isOpen={isConfirmationModalOpen}
                onHide={handleConfirmationHide}
                width={500}
                buttons={[
                    <SvyButton key="close" onClick={forceHide} color="destructive">
                        <FormattedMessage id="button.close" />
                    </SvyButton>,
                ]}
            >
                <FormattedMessage id="form.preventLeave" />
            </Modal>
            <FormProvider {...form}>
                <InputsContainer>
                    <Controller
                        control={form.control}
                        name="firstName"
                        render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                            <Input
                                onChange={onChange}
                                value={value}
                                labelId="user.create.firstName"
                                error={fieldError?.message}
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name="lastName"
                        render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                            <Input
                                onChange={onChange}
                                value={value}
                                labelId="user.create.lastName"
                                error={fieldError?.message}
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name="phone"
                        render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                            <Input
                                onChange={onChange}
                                value={value}
                                labelId="user.create.phone"
                                error={fieldError?.message}
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name="email"
                        render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                            <Input
                                onChange={onChange}
                                value={value}
                                name="email"
                                labelId="user.create.email"
                                error={fieldError?.message}
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name="letUserChoosePassword"
                        render={({ field: { onChange, value } }) => (
                            <SwitchWrapper>
                                <SvySwitch value={value || false} onChange={onChange} />
                                <Label labelId="user.create.letUserChoosePassword" />
                            </SwitchWrapper>
                        )}
                    />
                    {!letUserChoosePassword && (
                        <>
                            <Controller
                                control={form.control}
                                name={'password'}
                                render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                                    <Input
                                        onChange={onChange}
                                        value={value}
                                        type="password"
                                        labelId="user.create.password"
                                        error={fieldError?.message}
                                    />
                                )}
                            />
                            <Controller
                                control={form.control}
                                name={'confirmPassword'}
                                render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                                    <Input
                                        onChange={onChange}
                                        value={value}
                                        type="password"
                                        labelId="user.create.confirmPassword"
                                        error={fieldError?.message}
                                    />
                                )}
                            />
                        </>
                    )}
                </InputsContainer>
            </FormProvider>
        </Modal>
    );
};
export default CreateUserForm;
